import React, { useEffect, useState } from 'react'
import moment from 'moment'
import styled, { keyframes } from 'styled-components'
import { useApi } from '../../hooks/useApi'

interface PodStatus {
  app: string
  name: string
  status: string
  startTime: any
  ip: string
  containerName: string
  containerImage: string
  containerPorts: string[]
}

const Pods: React.FC = () => {
  const { callApi } = useApi()
  const [data, setData] = useState<PodStatus[] | null>(null)

  useEffect(() => {
    callApi('/admin/pods')
      .then(setData)
      .catch((e) => console.error('Error loading pods:', e))
  }, [])

  if (!data) return <LoaderBar />

  return (
    <TableWrap>
      <Table>
        <thead>
          <tr>
            <Th>Name</Th>
            <Th>App</Th>
            <Th>Status</Th>
            <Th>Start time</Th>
            <Th>IP</Th>
            <Th>Container</Th>
            <Th>Image</Th>
            <Th>Ports</Th>
          </tr>
        </thead>
        <tbody>
          {data.map((item, index) => {
            const isRunning = item.status === 'Running'
            const isSucceeded = item.status === 'Succeeded'

            return (
              <tr key={index}>
                <Td><PodName>{item.name}</PodName></Td>
                <Td><Mono>{item.app}</Mono></Td>
                <Td>
                  {isRunning && <RunningBadge>Running</RunningBadge>}
                  {isSucceeded && <StatusDot ok title="Succeeded" />}
                  {!isRunning && !isSucceeded && <ErrorBadge>{item.status}</ErrorBadge>}
                </Td>
                <Td><Mono>{moment(item.startTime).format('DD/MM/YY HH:mm')}</Mono></Td>
                <Td><Mono>{item.ip}</Mono></Td>
                <Td><Mono>{item.containerName}</Mono></Td>
                <Td><ImageText>{item.containerImage}</ImageText></Td>
                <Td><Mono>{item.containerPorts?.join(', ')}</Mono></Td>
              </tr>
            )
          })}
        </tbody>
      </Table>
    </TableWrap>
  )
}

export default Pods

// ─── Styles ──────────────────────────────────────────────────────────────────

const shimmer = keyframes`
  0% { transform: translateX(-100%); }
  100% { transform: translateX(100%); }
`

const LoaderBar = styled.div`
  height: 2px;
  background: rgba(255, 255, 255, 0.06);
  border-radius: 2px;
  overflow: hidden;
  position: relative;

  &::after {
    content: '';
    display: block;
    position: absolute;
    inset: 0;
    background: linear-gradient(90deg, transparent, rgba(111, 92, 230, 0.5), transparent);
    animation: ${shimmer} 1.4s ease infinite;
  }
`

const TableWrap = styled.div`
  overflow-x: auto;
  border: 1px solid rgba(255, 255, 255, 0.07);
  border-radius: 8px;
`

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
`

const Th = styled.th`
  padding: 10px 12px;
  text-align: left;
  color: #a1a1a1;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
`

const Td = styled.td`
  padding: 10px 12px;
  color: #a0a0a0;
  font-size: 13px;
  vertical-align: middle;
  border-bottom: 1px solid rgba(255, 255, 255, 0.04);
  tr:last-child & { border-bottom: none; }
`

const PodName = styled.span`
  font-family: 'SF Mono', 'Fira Code', monospace;
  font-size: 12px;
  color: #ededed;
`

const Mono = styled.span`
  font-family: 'SF Mono', 'Fira Code', monospace;
  font-size: 12px;
  color: #a0a0a0;
`

const ImageText = styled.span`
  font-family: 'SF Mono', 'Fira Code', monospace;
  font-size: 10px;
  color: #777;
  word-break: break-all;
`

const StatusDot = styled.span<{ ok: boolean }>`
  display: inline-block;
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: ${({ ok }) => ok ? '#33C2B0' : '#ff4444'};
`

const RunningBadge = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 11px;
  background: rgba(111, 92, 230, 0.1);
  color: #a89aff;
  border: 1px solid rgba(111, 92, 230, 0.25);
`

const ErrorBadge = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 11px;
  background: rgba(255, 68, 68, 0.1);
  color: #ff4444;
  border: 1px solid rgba(255, 68, 68, 0.25);
`
